import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import Layout from "../components/Layout/Layout";
import { useBetterAuthConvexAuth } from "../hooks/useAppAuth";

type TabId = "admins" | "test-user" | "customer-user";

interface Admin {
  _id: Id<"users">;
  username: string;
  email?: string;
  name?: string;
  role: "admin" | "super_admin";
  mobileNumber?: string;
  telephoneNumber?: string;
  createdAt: number;
}

const TAB_LABELS: Record<TabId, string> = {
  admins: "Manage Admins",
  "test-user": "Create Test User",
  "customer-user": "Create Site",
};

const normalizeTab = (value: string | null): TabId | null => {
  if (value === "admins" || value === "test-user" || value === "customer-user") {
    return value;
  }
  return null;
};

const ManageAdminsTab: React.FC = () => {
  const admins = useQuery(api.users.getAdmins) as Admin[] | undefined;
  const createAdminMutation = useMutation(api.users.createAdmin);
  const updateAdminMutation = useMutation(api.users.updateAdmin);
  const deleteAdminMutation = useMutation(api.users.deleteAdmin);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    name: "",
    mobile_number: "",
    telephone_number: "",
    role: "admin" as "admin" | "super_admin",
  });
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      name: "",
      mobile_number: "",
      telephone_number: "",
      role: "admin",
    });
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });
    setLoading(true);

    try {
      const result = await createAdminMutation({
        username: formData.username,
        email: formData.email,
        name: formData.name,
        mobileNumber: formData.mobile_number || undefined,
        telephoneNumber: formData.telephone_number || undefined,
        role: formData.role,
      });

      if (result.success) {
        setMessage({
          type: "success",
          text: result.pendingRoleRequest
            ? "Admin profile created. Role assignment is pending super-admin approval."
            : "Admin user created successfully!",
        });
        setShowCreateForm(false);
        resetForm();
      } else {
        setMessage({ type: "error", text: result.error || "Failed to create admin user" });
      }
    } catch (error: any) {
      setMessage({ type: "error", text: error.message || "Failed to create admin user" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAdmin) return;

    setMessage({ type: "", text: "" });
    setLoading(true);

    try {
      const result = await updateAdminMutation({
        id: editingAdmin._id,
        email: formData.email || undefined,
        name: formData.name || undefined,
        mobileNumber: formData.mobile_number || undefined,
        telephoneNumber: formData.telephone_number || undefined,
        role: formData.role,
      });

      if (result.success) {
        setMessage({
          type: "success",
          text: result.pendingRoleRequest
            ? "Admin details updated. Role change is pending super-admin approval."
            : "Admin user updated successfully!",
        });
        setEditingAdmin(null);
        resetForm();
      } else {
        setMessage({ type: "error", text: result.error || "Failed to update admin user" });
      }
    } catch (error: any) {
      setMessage({ type: "error", text: error.message || "Failed to update admin user" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (admin: Admin) => {
    if (!confirm(`Are you sure you want to delete admin user ${admin.username}?`)) {
      return;
    }

    try {
      const result = await deleteAdminMutation({ id: admin._id });
      if (result.success) {
        setMessage({ type: "success", text: "Admin user deleted successfully!" });
      } else {
        setMessage({ type: "error", text: result.error || "Failed to delete admin user" });
      }
    } catch (error: any) {
      setMessage({ type: "error", text: error.message || "Failed to delete admin user" });
    }
  };

  const startEdit = (admin: Admin) => {
    setEditingAdmin(admin);
    setFormData({
      username: admin.username,
      email: admin.email || "",
      name: admin.name || "",
      mobile_number: admin.mobileNumber || "",
      telephone_number: admin.telephoneNumber || "",
      role: admin.role,
    });
    setShowCreateForm(false);
  };

  if (admins === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-light">Manage Administrators</h2>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingAdmin(null);
            resetForm();
          }}
          className="bg-[#16A34A] text-white px-4 py-2 rounded-md hover:bg-shade-green-700"
        >
          Create New Admin
        </button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-md ${message.type === "success"
          ? "bg-green-50 text-green-800 border border-green-200"
          : "bg-red-50 text-red-800 border border-red-200"
          }`}>
          {message.text}
        </div>
      )}

      {(showCreateForm || editingAdmin) && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h3 className="text-xl font-medium font-sans mb-4">
            {editingAdmin ? "Edit Admin User" : "Create New Admin User"}
          </h3>

          <form onSubmit={editingAdmin ? handleUpdateAdmin : handleCreateAdmin} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={!!editingAdmin}
                  className="w-full"
                  pattern="[a-zA-Z0-9_]+"
                  title="Username must be alphanumeric (letters, numbers, underscores only)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number (Optional)</label>
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telephone Number (Optional)</label>
                <input
                  type="tel"
                  value={formData.telephone_number}
                  onChange={(e) => setFormData({ ...formData, telephone_number: e.target.value })}
                  className="w-full"
                />
              </div>
            </div>

            {!editingAdmin && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> After creating this admin profile, the user will need to sign up via Better Auth using the email address you specify. Their Better Auth account will be automatically linked to this profile on first sign-in.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={formData.role === "admin"}
                    onChange={() => setFormData({ ...formData, role: "admin" })}
                    className="mr-2"
                  />
                  <span className="font-medium">Admin</span>
                  <span className="text-gray-500 ml-2">- Can manage customers and view reports</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="super_admin"
                    checked={formData.role === "super_admin"}
                    onChange={() => setFormData({ ...formData, role: "super_admin" })}
                    className="mr-2"
                  />
                  <span className="font-medium">Super Admin</span>
                  <span className="text-gray-500 ml-2">- Full access including admin management</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingAdmin(null);
                  resetForm();
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#16A34A] text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? "Saving..." : editingAdmin ? "Update Admin" : "Create Admin"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {admins.map((admin) => (
              <tr key={admin._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{admin.username}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{admin.name || "-"}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{admin.email || "-"}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-medium font-sans rounded-full ${admin.role === "super_admin"
                    ? "bg-red-100 text-red-800"
                    : "bg-green-100 text-green-800"
                    }`}>
                    {admin.role === "super_admin" ? "Super Admin" : "Admin"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(admin.createdAt).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => startEdit(admin)}
                    className="text-shade-green-600 hover:text-shade-green-900 mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteAdmin(admin)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CreateTestUserTab: React.FC = () => {
  const createUser = useMutation(api.authFunctions.createUserAccount);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: "",
    email: "",
    name: "",
    password: "",
    verifyPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const quickFill = () => {
    const n = Date.now().toString().slice(-6);
    setForm({
      username: `testsite${n}`,
      email: `test.mobile.${n}@example.com`,
      name: `Test User ${n}`,
      password: `TestMeter${n}!`,
      verifyPassword: `TestMeter${n}!`,
    });
    setMessage("");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      if (form.password !== form.verifyPassword) {
        setMessage("Passwords do not match. Please re-enter and verify.");
        setLoading(false);
        return;
      }
      await createUser({
        username: form.username.trim().toLowerCase(),
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: "customer",
      });
      setMessage(`Created. Login with username "${form.username}" and password "${form.password}"`);
    } catch (err: any) {
      setMessage(err?.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-medium font-sans mb-4">Create Test Customer</h2>

      <div className="mb-4">
        <button onClick={quickFill} className="bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-700">
          Quick Fill Test Data
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <input className="w-full border rounded-md p-2" placeholder="Username" value={form.username}
          onChange={(e) => setForm({ ...form, username: e.target.value })} required />
        <input className="w-full border rounded-md p-2" placeholder="Email" type="email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} required />
        <input className="w-full border rounded-md p-2" placeholder="Name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input className="w-full border rounded-md p-2" placeholder="Password" type={showPassword ? "text" : "password"} value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} required />
        <input className="w-full border rounded-md p-2" placeholder="Re-Verify Password" type={showPassword ? "text" : "password"} value={form.verifyPassword}
          onChange={(e) => setForm({ ...form, verifyPassword: e.target.value })} required />

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-700">Show password</label>
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
          />
        </div>
        <button type="submit" disabled={loading} className="bg-[#16A34A] text-white px-4 py-2 rounded-md hover:bg-shade-green-700 disabled:opacity-50">
          {loading ? "Creating..." : "Create Test User"}
        </button>
      </form>

      {message && <p className="mt-4 text-sm">{message}</p>}
    </div>
  );
};

const CreateCustomerUserTab: React.FC = () => {
  const createCustomer = useMutation(api.users.createCustomer);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    username: "",
    email: "",
    email2: "",
    meterSerialNumber: "",
    mobileNumber: "",
    name: "",
    telephoneNumber: "",
    address: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    const requiredFields: Array<[string, string]> = [
      ["Site Name", form.username],
      ["Meter Serial Number", form.meterSerialNumber],
      ["Full Name", form.name],
      ["Mobile Number", form.mobileNumber],
      ["Email Address", form.email],
      ["Telephone Number", form.telephoneNumber],
      ["Address", form.address],
    ];
    const missing = requiredFields.filter(([, value]) => !value.trim()).map(([label]) => label);
    if (missing.length > 0) {
      setMessage({ type: "error", text: `Please fill in: ${missing.join(", ")}.` });
      return;
    }

    setLoading(true);

    try {
      const result = await createCustomer({
        username: form.username.trim().toLowerCase(),
        email: form.email.trim().toLowerCase() || undefined,
        email2: form.email2.trim().toLowerCase() || undefined,
        meterSerialNumber: form.meterSerialNumber.trim(),
        mobileNumber: form.mobileNumber.trim() || undefined,
        name: form.name.trim() || undefined,
        telephoneNumber: form.telephoneNumber.trim() || undefined,
        address: form.address.trim() || undefined,
      });

      if (result.success) {
        setMessage({
          type: "success",
          text: `Created customer "${form.username}" with linked Better Auth account.`,
        });
        setForm({
          username: "",
          email: "",
          email2: "",
          meterSerialNumber: "",
          mobileNumber: "",
          name: "",
          telephoneNumber: "",
          address: "",
        });
      } else {
        setMessage({ type: "error", text: result.error || "Failed to create site" });
      }
    } catch (error: any) {
      setMessage({ type: "error", text: error?.message || "Failed to create site" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-medium font-sans mb-4">Create Site</h2>

      {message.text && (
        <div className={`mb-6 p-4 rounded-md ${message.type === "success"
          ? "bg-green-50 text-green-800 border border-green-200"
          : "bg-red-50 text-red-800 border border-red-200"
          }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name <span className="text-red-500">*</span></label>
            <input
              className="w-full border rounded-md p-2"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })}
              pattern="[a-zA-Z0-9_.\-/]+"
              title="Username can contain letters, numbers, underscores, periods, hyphens, and slashes"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meter Serial Number  <span className="text-red-500">*</span> (Password)</label>
            <input
              className="w-full border rounded-md p-2"
              type={showPassword ? "text" : "password"}
              value={form.meterSerialNumber}
              onChange={(e) => setForm({ ...form, meterSerialNumber: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name  <span className="text-red-500">*</span></label>
            <input
              className="w-full border rounded-md p-2"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number  <span className="text-red-500">*</span></label>
            <input
              className="w-full border rounded-md p-2"
              type="tel"
              value={form.mobileNumber}
              onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address  <span className="text-red-500">*</span></label>
            <input
              className="w-full border rounded-md p-2"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Email (Optional)</label>
            <input
              className="w-full border rounded-md p-2"
              type="email"
              value={form.email2}
              onChange={(e) => setForm({ ...form, email2: e.target.value.toLowerCase() })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telephone Number <span className="text-red-500">*</span></label>
            <input
              className="w-full border rounded-md p-2"
              type="tel"
              value={form.telephoneNumber}
              onChange={(e) => setForm({ ...form, telephoneNumber: e.target.value })}
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address <span className="text-red-500">*</span></label>
            <textarea
              className="w-full border rounded-md p-2"
              rows={3}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              required
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-700">Show meter serial number</label>
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(e) => setShowPassword(e.target.checked)}
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#16A34A] text-white px-4 py-2 rounded-md hover:bg-shade-green-700 disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create Site"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ManageUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, isLoadingProfile } = useBetterAuthConvexAuth();

  const allowedTabs = useMemo(
    () => [
      ...(isSuperAdmin ? (["admins"] as TabId[]) : []),
      "test-user" as TabId,
      ...(isSuperAdmin ? (["customer-user"] as TabId[]) : []),
    ],
    [isSuperAdmin],
  );

  // Preserve the requested tab until the profile resolves, so a super admin
  // deep-linking to a gated tab isn't bounced to the default before
  // isSuperAdmin is known.
  const requestedTab = normalizeTab(searchParams.get("tab"));
  const activeTab = requestedTab && (isLoadingProfile || allowedTabs.includes(requestedTab))
    ? requestedTab
    : allowedTabs[0];

  useEffect(() => {
    if (isLoadingProfile) return;
    if (searchParams.get("tab") !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, isLoadingProfile, searchParams, setSearchParams]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-shade-green-500 transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Admin
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-light mb-6">Manage Users</h1>
          {isLoadingProfile ? (
            <p className="text-sm text-gray-500 py-3">Loading…</p>
          ) : (
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-6" aria-label="Manage user tabs">
              {allowedTabs.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setSearchParams({ tab })}
                  className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${activeTab === tab
                    ? "border-shade-green-600 text-shade-green-700"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                >
                  {TAB_LABELS[tab]}
                </button>
              ))}
            </nav>
          </div>
          )}
        </div>

        {!isLoadingProfile && activeTab === "admins" && isSuperAdmin && <ManageAdminsTab />}
        {!isLoadingProfile && activeTab === "test-user" && <CreateTestUserTab />}
        {!isLoadingProfile && activeTab === "customer-user" && isSuperAdmin && <CreateCustomerUserTab />}
      </div>
    </Layout>
  );
};

export default ManageUsersPage;
